import { useState } from 'react';
import {
  Button,
  Chip,
  FilterBar,
  Pill,
  RenewalTimeline,
  Screen,
  ScreenHeader,
  StateBox,
  SummaryTiles,
  TierBadge,
  type PillTone,
} from '@garnet/ui';
import type { Credential, RenewalStateVM, TpmTier, VerticalData } from '../data/types.js';

/* Garnet · Renewals calendar */

const LEAD: Record<TpmTier, number> = { critical: 90, high: 60, standard: 30, low: 14 };
const RST: Record<RenewalStateVM, { tone: PillTone; label: string }> = {
  expired: { tone: 'f', label: 'expired' },
  expiring: { tone: 'a', label: 'expiring' },
  valid: { tone: 'm', label: 'valid' },
};

function RStatePill({ st }: { st: RenewalStateVM }) {
  const m = RST[st];
  return <Pill tone={m.tone}>{m.label}</Pill>;
}

function dueLabel(c: Credential): string {
  if (c.days < 0) return Math.abs(c.days) + ' days ago';
  if (c.days === 0) return 'today';
  return 'in ' + c.days + ' days';
}

function RenewRow({ c }: { c: Credential }) {
  const lead = LEAD[c.tier];
  const inLead = c.days <= lead;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 360px 150px', gap: 20, alignItems: 'center',
      padding: '16px 18px', borderBottom: '1px solid var(--rule)' }}>
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 500, fontSize: 14 }}>{c.cred}</span>
          <TierBadge level={c.tier} sm />
        </div>
        <div style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>{c.supplier} · {c.scheme}</div>
      </div>
      <RenewalTimeline name={c.cred} meta={dueLabel(c)} state={c.st} pct={c.pct} />
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
        <RStatePill st={c.st} />
        {c.st === 'valid'
          ? <span style={{ fontFamily: 'var(--mono)', fontSize: 10.5, color: 'var(--muted)' }}>{inLead ? 'lead window open' : 'lead opens at ' + lead + 'd'}</span>
          : <Button sm variant={c.st === 'expired' ? 'primary' : 'secondary'} icon={c.st === 'expired' ? 'refresh' : 'clock'}>{c.st === 'expired' ? 'Re-verify' : 'Open renewal'}</Button>}
      </div>
    </div>
  );
}

const TIER_FILTERS: Array<'all' | TpmTier> = ['all', 'critical', 'high', 'standard', 'low'];
const STATE_FILTERS: Array<'all' | RenewalStateVM> = ['all', 'expired', 'expiring', 'valid'];

export function Renewals({ data }: { data: VerticalData }) {
  const [tier, setTier] = useState<'all' | TpmTier>('all');
  const [state, setState] = useState<'all' | RenewalStateVM>('all');

  const rows = data.credentials
    .filter((c) => tier === 'all' || c.tier === tier)
    .filter((c) => state === 'all' || c.st === state)
    .slice()
    .sort((a, b) => a.days - b.days);

  const sum: Record<RenewalStateVM, number> = { expired: 0, expiring: 0, valid: 0 };
  data.credentials.forEach((c) => { sum[c.st]++; });

  const tiles: Array<[RenewalStateVM, number, PillTone]> = [
    ['expired', sum.expired, 'f'],
    ['expiring', sum.expiring, 'a'],
    ['valid', sum.valid, 'm'],
  ];

  return (
    <Screen>
      <ScreenHeader
        eyebrow="§ Renewals"
        title="Credential validity, across suppliers"
        lead="Every credential is renewal-tracked. An expiring credential surfaces here before it lapses; the TPM tier sets the lead time, so a critical supplier surfaces earlier than a low one."
      />

      {/* summary */}
      <SummaryTiles>
        {tiles.map(([k, v, tone]) => (
          <div key={k} className="stat-tile" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Pill tone={tone}>{k}</Pill>
            <span style={{ fontFamily: 'var(--display)', fontSize: '1.7rem', fontWeight: 700 }}>{v}</span>
          </div>
        ))}
      </SummaryTiles>

      {/* filters */}
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <FilterBar label="tier">
          {TIER_FILTERS.map((x) => (
            <Chip key={x} active={tier === x} onClick={() => setTier(x)}>{x}</Chip>
          ))}
        </FilterBar>
        <FilterBar label="state">
          {STATE_FILTERS.map((x) => (
            <Chip key={x} active={state === x} onClick={() => setState(x)}>{x}</Chip>
          ))}
        </FilterBar>
      </div>

      {/* timeline list */}
      <div className="panel">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 360px 150px', gap: 20, padding: '10px 18px',
          borderBottom: '1px solid var(--ink)', fontFamily: 'var(--mono)', fontSize: 10.5, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--muted)' }}>
          <span>credential · supplier</span><span>validity track</span><span style={{ textAlign: 'right' }}>state · task</span>
        </div>
        {rows.length === 0
          ? <div style={{ padding: 24 }}><StateBox kind="empty" title="No credentials in this filter" /></div>
          : rows.map((c) => <RenewRow key={c.id} c={c} />)}
      </div>
    </Screen>
  );
}
